import { useCallback, useState } from "react"

import { Link, useNavigate } from "react-router-dom"

import type {
  BatteryProposalDto,
  BatteryProposalFinancingDto,
} from "~/api/dto/battery-proposal.dto"

import { LIGHTREACH_RESERVE_NOW_REASON_FALLBACK } from "./lightreach-battery-copy"
import { LightReachBatteryApplication } from "./LightReachBatteryApplication"
import { isBatteryProposalReadOnly } from "../../battery-proposal-read-only"
import { RESERVE_NOW_LABEL } from "../../contract/contract-action"
import { SelectLoanProductAction } from "../../select-loan-product-action"
import {
  batteryFinancingStatusPath,
  batteryProposalPath,
} from "../../setup/setup-steps"
import { APPLICATION_STATUS_ACTION_LABEL } from "../application-status-labels"
import type { BatteryDrawerLaunchState } from "../battery-drawer-launch"

const pillClass = `font-display inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-[length:var(--step-0)] font-bold text-bp-accent shadow-[0_12px_32px_rgba(0,0,0,0.25)] hover:bg-white/90 focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60`

const hasLightReachApplication = (
  financing: BatteryProposalFinancingDto,
): boolean =>
  financing.actions.viewLightReachApplication?.allowed === true ||
  financing.currentApplication?.provider === `lightreach` ||
  financing.previousApplications.some(
    (application) => application.provider === `lightreach`,
  )

/**
 * Reserve now for the LightReach (Battery Only) plan. It opens the LightReach
 * credit application once the server allows a start (a saved, current offer
 * and no application yet). After submission the entry becomes Application
 * status, which opens the financing-status drawer.
 */
export function LightReachBatteryReserveNow({
  proposal,
  financing,
  needsLoanProduct,
}: {
  proposal: BatteryProposalDto
  financing: BatteryProposalFinancingDto
  needsLoanProduct: boolean
}): JSX.Element | null {
  const proposalId = proposal.id
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const closeApplication = useCallback(() => setOpen(false), [])
  const readOnly = isBatteryProposalReadOnly(proposal)
  const hasApplication = hasLightReachApplication(financing)

  const statusState = {
    returnTo: batteryProposalPath(proposalId),
  } satisfies BatteryDrawerLaunchState

  // The submit's response is the financing read, so the drawer opens on the
  // application's status.
  const onSubmitted = (): void => {
    setOpen(false)
    navigate(batteryFinancingStatusPath(proposalId), { state: statusState })
  }

  if (readOnly && !hasApplication) {
    return null
  }

  const startAction = financing.actions.startLightReachApplication
  const canStart = startAction?.allowed === true
  const reason = canStart
    ? null
    : (startAction?.reason ?? LIGHTREACH_RESERVE_NOW_REASON_FALLBACK)

  const entry = hasApplication ? (
    <div
      data-testid="battery-existing-loan-application"
      className="relative mt-7 flex flex-col items-start gap-3 print:hidden"
    >
      <Link
        to={batteryFinancingStatusPath(proposalId)}
        state={statusState}
        data-testid="battery-financial-summary-application-status"
        className={pillClass}
      >
        {APPLICATION_STATUS_ACTION_LABEL}
        <span aria-hidden="true">→</span>
      </Link>
    </div>
  ) : needsLoanProduct ? (
    <SelectLoanProductAction proposalId={proposalId} />
  ) : (
    <div
      data-testid="battery-lightreach-reserve-now"
      className="relative mt-7 flex flex-col items-start gap-3 print:hidden"
    >
      <button
        type="button"
        disabled={!canStart}
        onClick={() => setOpen(true)}
        data-testid="battery-lightreach-reserve-now-button"
        className={pillClass}
      >
        {RESERVE_NOW_LABEL}
        <span aria-hidden="true">→</span>
      </button>
      {reason != null ? (
        <p
          data-testid="battery-lightreach-reserve-now-reason"
          className="text-[length:var(--step--2)] text-white/85"
        >
          {reason}
        </p>
      ) : null}
    </div>
  )

  // The dialog keeps one place in the tree and stays mounted until the seller
  // closes it, even once the financing read reports the application: a
  // submission LightReach did not confirm still holds a row, and its warning
  // must stay readable.
  return (
    <>
      {entry}
      {open ? (
        <LightReachBatteryApplication
          proposal={proposal}
          onClose={closeApplication}
          onSubmitted={onSubmitted}
        />
      ) : null}
    </>
  )
}
